<template>
  <div class="plans-page">

    <!-- Header -->
    <div class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">Gestão de Planos</div>
          <div class="page-subtitle">Planos de subscrição e preços</div>
        </div>
        <div class="header-actions">
          <button class="btn-primary" @click="openCreate">
            <q-icon name="add" size="16px" class="q-mr-xs" />
            Novo Plano
          </button>
        </div>
      </div>
    </div>

    <div class="content-wrapper">

      <!-- Loading -->
      <div v-if="loading">
        <div v-for="n in 4" :key="n" class="skeleton-row q-mb-sm">
          <q-skeleton type="rect" height="60px" style="border-radius:12px" />
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="plans.length === 0" class="empty-state">
        <q-icon name="layers" size="56px" color="grey-4" />
        <div class="empty-text">Nenhum plano encontrado</div>
        <button class="btn-primary q-mt-md" @click="openCreate">Criar primeiro plano</button>
      </div>

      <!-- Plans table -->
      <div v-else class="plans-list">
        <div class="plans-table-head">
          <div class="col-name">Plano</div>
          <div class="col-slug">Slug</div>
          <div class="col-centre">Activo</div>
          <div class="col-centre">Negociável</div>
          <div class="col-centre">Ordem</div>
          <div class="col-centre">Subscrições</div>
          <div class="col-actions">Acções</div>
        </div>

        <div v-for="plan in plans" :key="plan.id" class="plan-row">
          <div class="col-name">
            <div class="plan-name">{{ plan.name }}</div>
            <div v-if="plan.description" class="plan-desc">{{ plan.description }}</div>
          </div>
          <div class="col-slug">
            <code class="slug-code">{{ plan.slug }}</code>
          </div>
          <div class="col-centre">
            <q-toggle
              :model-value="activeStates[plan.id] ?? true"
              color="positive"
              @update:model-value="toggleActive(plan, $event)"
            />
          </div>
          <div class="col-centre">
            <span v-if="plan.is_negotiable" class="badge badge--warn">Sim</span>
            <span v-else class="badge badge--muted">Não</span>
          </div>
          <div class="col-centre text-grey-6">{{ plan.sort_order ?? '—' }}</div>
          <div class="col-centre">
            <span class="badge badge--info">{{ plan.active_subscriptions_count ?? '—' }}</span>
          </div>
          <div class="col-actions">
            <button class="action-icon action-icon--primary" @click="openEdit(plan)">
              <q-icon name="edit" size="17px" />
              <q-tooltip>Editar</q-tooltip>
            </button>
            <button class="action-icon action-icon--prices" @click="openPrices(plan)">
              <q-icon name="sell" size="17px" />
              <q-tooltip>Preços</q-tooltip>
            </button>
            <button class="action-icon action-icon--danger" @click="confirmDelete(plan)">
              <q-icon name="delete" size="17px" />
              <q-tooltip>Eliminar</q-tooltip>
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- ── Plan form modal ───────────────────────────────── -->
    <q-dialog v-model="planDialog" persistent maximized>
      <q-card class="plan-dialog">
        <q-card-section class="dialog-header row items-center">
          <div class="dialog-title">{{ editingPlan ? 'Editar Plano' : 'Novo Plano' }}</div>
          <q-space />
          <q-btn flat round dense icon="close" v-close-popup color="grey-5" />
        </q-card-section>

        <q-card-section class="dialog-body">
          <div class="form-grid">

            <!-- Basic info -->
            <div class="form-section">
              <div class="form-section-title">Informação básica</div>
              <div class="form-row-2">
                <q-input v-model="planForm.name" label="Nome do plano *" outlined dense @update:model-value="autoSlug" />
                <q-input v-model="planForm.slug" label="Slug *" outlined dense hint="URL amigável, único" />
              </div>
              <q-input v-model="planForm.description" label="Descrição" outlined dense type="textarea" rows="2" class="q-mt-sm" />
              <div class="form-row-3 q-mt-sm">
                <q-input v-model.number="planForm.sort_order" label="Ordem" outlined dense type="number" />
                <div class="q-pt-xs">
                  <q-toggle v-model="planForm.is_active" label="Activo" color="positive" />
                </div>
                <div class="q-pt-xs">
                  <q-toggle v-model="planForm.is_negotiable" label="Negociável (Enterprise)" color="warning" />
                </div>
              </div>
            </div>

            <div class="form-section">
              <div class="form-section-title">Preço dos slots extra</div>
              <p class="form-section-copy">Preço por slot adicional. Aplica-se ao catálogo escolhido na compra.</p>
              <div class="form-row-2">
                <q-input v-model.number="planForm.feature.extra_slot_price" label="Preço slot extra (MZN)" outlined dense type="number" />
              </div>
            </div>

            <div class="form-section form-section--catalogs">
              <div class="form-section-title">Configuração por tipo de anúncio</div>
              <p class="form-section-copy">Escolha os catálogos incluídos no plano e defina os limites e benefícios de cada um.</p>
              <div class="listing-feature-grid">
                <article
                  v-for="listingFeature in planForm.listing_features"
                  :key="listingFeature.listing_type"
                  class="listing-feature-card"
                  :class="{ 'listing-feature-card--disabled': !listingFeature.enabled }"
                >
                  <header class="listing-feature-card__header">
                    <div class="listing-feature-card__identity">
                      <q-icon :name="listingTypeMeta[listingFeature.listing_type].icon" size="23px" />
                      <div>
                        <strong>{{ listingTypeMeta[listingFeature.listing_type].title }}</strong>
                        <small>{{ listingTypeMeta[listingFeature.listing_type].description }}</small>
                      </div>
                    </div>
                    <q-toggle v-model="listingFeature.enabled" color="positive" label="Incluído" />
                  </header>

                  <div class="listing-feature-card__body">
                    <div class="form-row-2">
                      <q-input v-model.number="listingFeature.max_active_listings" :disable="!listingFeature.enabled" label="Anúncios activos máx." outlined dense type="number" min="0" />
                      <q-input v-model.number="listingFeature.max_photos" :disable="!listingFeature.enabled" label="Fotos máx. por anúncio" outlined dense type="number" min="1" />
                    </div>
                    <div class="form-row-2 q-mt-sm">
                      <q-input v-model.number="listingFeature.priority_weight" :disable="!listingFeature.enabled" label="Peso de prioridade" outlined dense type="number" min="0" />
                      <q-input v-model="listingFeature.badge_label" :disable="!listingFeature.enabled" label="Texto do selo" outlined dense placeholder="Ex.: Destaque" />
                    </div>
                    <div class="listing-feature-card__toggles">
                      <q-toggle v-model="listingFeature.is_featured_home" :disable="!listingFeature.enabled" label="Destaque na home" color="primary" />
                      <q-toggle v-model="listingFeature.shows_above_normal" :disable="!listingFeature.enabled" label="Acima do normal" color="primary" />
                      <q-toggle v-model="listingFeature.auto_social_share" :disable="!listingFeature.enabled" label="Partilha social" color="primary" />
                      <q-toggle v-model="listingFeature.whatsapp_button" :disable="!listingFeature.enabled" label="WhatsApp" color="positive" />
                    </div>
                  </div>
                </article>
              </div>
            </div>

            <!-- Perks -->
            <div class="form-section">
              <div class="form-section-title">Perks (vantagens)</div>
              <div v-for="(perk, i) in planForm.feature.perks" :key="i" class="perk-row">
                <q-input v-model="planForm.feature.perks[i]" :label="`Perk ${i + 1}`" outlined dense class="flex-1" />
                <button class="perk-remove" @click="removePerk(i)">
                  <q-icon name="close" size="16px" />
                </button>
              </div>
              <button class="btn-add-perk q-mt-xs" @click="addPerk">
                <q-icon name="add" size="14px" class="q-mr-xs" />
                Adicionar perk
              </button>
            </div>

            <!-- Prices (only if not negotiable) -->
            <div class="form-section" v-if="!planForm.is_negotiable && !editingPlan">
              <div class="form-section-title">Preços iniciais</div>
              <div class="price-table-head">
                <span>Duração (dias)</span>
                <span>Preço</span>
                <span>Moeda</span>
                <span></span>
              </div>
              <div v-for="(price, i) in planForm.prices" :key="i" class="price-table-row">
                <q-input v-model.number="price.duration_days" outlined dense type="number" placeholder="30" />
                <q-input v-model.number="price.price" outlined dense type="number" placeholder="1500" />
                <q-input v-model="price.currency" outlined dense placeholder="MZN" style="max-width:80px" />
                <button class="perk-remove" @click="removePrice(i)">
                  <q-icon name="close" size="16px" />
                </button>
              </div>
              <button class="btn-add-perk q-mt-xs" @click="addPrice">
                <q-icon name="add" size="14px" class="q-mr-xs" />
                Adicionar preço
              </button>
            </div>

          </div>
        </q-card-section>

        <q-card-actions align="right" class="dialog-footer">
          <q-btn flat label="Cancelar" v-close-popup color="grey-6" />
          <q-btn
            unelevated
            :label="editingPlan ? 'Guardar alterações' : 'Criar plano'"
            color="primary"
            :loading="saving"
            @click="savePlan"
            style="border-radius:8px"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- ── Prices modal ──────────────────────────────────── -->
    <q-dialog v-model="pricesDialog" persistent>
      <q-card style="min-width:540px; max-width:620px; border-radius:16px;">
        <q-card-section class="dialog-header row items-center">
          <div class="dialog-title">Preços — {{ selectedPlan?.name }}</div>
          <q-space />
          <q-btn flat round dense icon="close" v-close-popup color="grey-5" />
        </q-card-section>

        <q-card-section>
          <div class="price-table-head">
            <span>Duração (dias)</span>
            <span>Preço</span>
            <span>Moeda</span>
            <span>Activo</span>
            <span></span>
          </div>

          <div v-if="pricesLoading" class="q-py-md text-center">
            <q-spinner color="primary" size="28px" />
          </div>

          <div v-else>
            <div v-for="price in planPrices" :key="price.id" class="price-edit-row">
              <q-input v-model.number="price.duration_days" outlined dense type="number" style="width:110px" readonly />
              <q-input v-model.number="price.price" outlined dense type="number" style="width:110px" @blur="savePrice(price)" />
              <q-input v-model="price.currency" outlined dense style="width:70px" @blur="savePrice(price)" />
              <q-toggle v-model="price.is_active" color="positive" @update:model-value="savePrice(price)" />
              <button class="perk-remove" @click="deletePrice(price)">
                <q-icon name="delete" size="16px" />
              </button>
            </div>
          </div>

          <!-- Add new price row -->
          <div class="price-table-row q-mt-sm" v-if="!selectedPlan?.is_negotiable">
            <q-input v-model.number="newPrice.duration_days" outlined dense type="number" placeholder="Dias" style="width:110px" />
            <q-input v-model.number="newPrice.price" outlined dense type="number" placeholder="Preço" style="width:110px" />
            <q-input v-model="newPrice.currency" outlined dense placeholder="MZN" style="width:70px" />
            <button class="btn-add-perk" @click="createPrice" :disabled="savingPrice">
              <q-icon name="add" size="14px" class="q-mr-xs" />
              Adicionar
            </button>
          </div>
        </q-card-section>

        <q-card-actions align="right" class="q-pa-md">
          <q-btn flat label="Fechar" v-close-popup color="grey-6" />
        </q-card-actions>
      </q-card>
    </q-dialog>

  </div>
</template>

<script>
import { ref, computed, reactive, watch, onMounted } from 'vue';
import store from '@/vuex/store';
import Swal from 'sweetalert2';

const defaultFeature = () => ({
  max_active_listings: 5,
  max_photos: 10,
  extra_slot_price: null,
  priority_weight: 1,
  is_featured_home: false,
  shows_above_normal: false,
  auto_social_share: false,
  whatsapp_button: false,
  perks: [],
});

const listingTypeMeta = {
  property: { title: 'Imóveis', description: 'Venda e arrendamento de propriedades', icon: 'home_work' },
  accommodation: { title: 'Alojamentos', description: 'Hotéis, casas, quartos, resorts e lodges', icon: 'hotel' },
  rental_vehicle: { title: 'Rent-a-Car', description: 'Viaturas com ou sem motorista', icon: 'directions_car' },
};

const defaultListingFeature = (listingType, base = defaultFeature()) => ({
  listing_type: listingType,
  enabled: true,
  max_active_listings: base.max_active_listings ?? 1,
  max_photos: base.max_photos ?? 6,
  priority_weight: base.priority_weight ?? 0,
  is_featured_home: !!base.is_featured_home,
  shows_above_normal: !!base.shows_above_normal,
  auto_social_share: !!base.auto_social_share,
  whatsapp_button: base.whatsapp_button !== false,
  badge_label: '',
});

const buildListingFeatures = (features = [], base = defaultFeature()) =>
  Object.keys(listingTypeMeta).map((listingType) => {
    const saved = features.find((feature) => feature.listing_type === listingType);
    return saved ? { ...defaultListingFeature(listingType, base), ...saved } : defaultListingFeature(listingType, base);
  });

const defaultForm = () => ({
  name: '',
  slug: '',
  description: '',
  is_active: true,
  is_negotiable: false,
  sort_order: 0,
  feature: defaultFeature(),
  listing_features: buildListingFeatures(),
  prices: [],
});

export default {
  name: 'AdminPlans',

  setup() {
    const loading = ref(false);
    const saving = ref(false);
    const planDialog = ref(false);
    const pricesDialog = ref(false);
    const editingPlan = ref(null);
    const selectedPlan = ref(null);
    const planForm = ref(defaultForm());
    const planPrices = ref([]);
    const pricesLoading = ref(false);
    const savingPrice = ref(false);
    const newPrice = ref({ duration_days: null, price: null, currency: 'MZN' });

    const plans = computed(() => store.state.plans.adminPlans);
    const activeStates = reactive({});

    watch(plans, (newPlans) => {
      newPlans.forEach((p) => {
        if (!(p.id in activeStates)) {
          activeStates[p.id] = p.is_active != null ? !!p.is_active : true;
        }
      });
    }, { immediate: true });

    const fetchPlans = async () => {
      loading.value = true;
      try {
        await store.dispatch('plans/getAdminPlans');
      } finally {
        loading.value = false;
      }
    };

    const autoSlug = (val) => {
      if (editingPlan.value) return;
      planForm.value.slug = (val || '')
        .toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    };

    const openCreate = () => {
      editingPlan.value = null;
      planForm.value = defaultForm();
      planDialog.value = true;
    };

    const openEdit = (plan) => {
      editingPlan.value = plan;
      planForm.value = {
        name: plan.name,
        slug: plan.slug,
        description: plan.description || '',
        is_active: !!plan.is_active,
        is_negotiable: !!plan.is_negotiable,
        sort_order: plan.sort_order ?? 0,
        feature: {
          max_active_listings: plan.feature?.max_active_listings ?? 5,
          max_photos: plan.feature?.max_photos ?? 10,
          extra_slot_price: plan.feature?.extra_slot_price ?? null,
          priority_weight: plan.feature?.priority_weight ?? 1,
          is_featured_home: !!plan.feature?.is_featured_home,
          shows_above_normal: !!plan.feature?.shows_above_normal,
          auto_social_share: !!plan.feature?.auto_social_share,
          whatsapp_button: !!plan.feature?.whatsapp_button,
          perks: [...(plan.feature?.perks || [])],
        },
        listing_features: buildListingFeatures(plan.listing_features || [], plan.feature || defaultFeature()),
        prices: [],
      };
      planDialog.value = true;
    };

    const openPrices = async (plan) => {
      selectedPlan.value = plan;
      pricesDialog.value = true;
      pricesLoading.value = true;
      newPrice.value = { duration_days: null, price: null, currency: 'MZN' };
      try {
        planPrices.value = await store.dispatch('plans/getPlanPrices', plan.id);
      } finally {
        pricesLoading.value = false;
      }
    };

    const addPerk = () => planForm.value.feature.perks.push('');
    const removePerk = (i) => planForm.value.feature.perks.splice(i, 1);
    const addPrice = () => planForm.value.prices.push({ duration_days: null, price: null, currency: 'MZN' });
    const removePrice = (i) => planForm.value.prices.splice(i, 1);

    const savePlan = async () => {
      if (!planForm.value.name || !planForm.value.slug) {
        Swal.fire({ title: 'Campos obrigatórios', text: 'Nome e slug são obrigatórios.', icon: 'warning' });
        return;
      }
      saving.value = true;
      try {
        const payload = {
          name: planForm.value.name,
          slug: planForm.value.slug,
          description: planForm.value.description,
          is_active: planForm.value.is_active,
          is_negotiable: planForm.value.is_negotiable,
          sort_order: planForm.value.sort_order,
          feature: {
            extra_slot_price: planForm.value.feature.extra_slot_price,
            perks: planForm.value.feature.perks,
          },
          listing_features: planForm.value.listing_features,
        };

        if (editingPlan.value) {
          await store.dispatch('plans/updatePlan', { id: editingPlan.value.id, payload });
          Swal.fire({ title: 'Plano actualizado!', icon: 'success', timer: 1800, showConfirmButton: false });
        } else {
          if (!planForm.value.is_negotiable) payload.prices = planForm.value.prices;
          await store.dispatch('plans/createPlan', payload);
          await fetchPlans();
          Swal.fire({ title: 'Plano criado!', icon: 'success', timer: 1800, showConfirmButton: false });
        }
        planDialog.value = false;
      } catch (err) {
        Swal.fire({ title: 'Erro', text: err.response?.data?.message || 'Não foi possível guardar o plano.', icon: 'error' });
      } finally {
        saving.value = false;
      }
    };

    const toggleActive = async (plan, val) => {
      const original = activeStates[plan.id];
      activeStates[plan.id] = val;
      try {
        await store.dispatch('plans/updatePlan', { id: plan.id, payload: { is_active: val } });
      } catch {
        activeStates[plan.id] = original;
        Swal.fire({ title: 'Erro', text: 'Não foi possível alterar o estado do plano.', icon: 'error' });
      }
    };

    const confirmDelete = (plan) => {
      Swal.fire({
        title: 'Eliminar plano?',
        text: `Tem a certeza que quer eliminar "${plan.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sim, eliminar',
        cancelButtonText: 'Cancelar',
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        try {
          await store.dispatch('plans/deletePlan', plan.id);
          Swal.fire({ title: 'Eliminado!', icon: 'success', timer: 1500, showConfirmButton: false });
        } catch (err) {
          Swal.fire({ title: 'Não foi possível eliminar', text: err.response?.data?.message || 'Erro inesperado.', icon: 'error' });
        }
      });
    };

    const savePrice = async (price) => {
      try {
        await store.dispatch('plans/updatePlanPrice', {
          planId: selectedPlan.value.id,
          priceId: price.id,
          payload: { price: price.price, currency: price.currency, is_active: price.is_active },
        });
      } catch {
        Swal.fire({ title: 'Erro', text: 'Não foi possível actualizar o preço.', icon: 'error' });
      }
    };

    const deletePrice = (price) => {
      Swal.fire({
        title: 'Eliminar preço?',
        text: `${price.duration_days} dias — ${price.price} ${price.currency}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Eliminar',
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        try {
          await store.dispatch('plans/deletePlanPrice', { planId: selectedPlan.value.id, priceId: price.id });
          planPrices.value = planPrices.value.filter((p) => p.id !== price.id);
        } catch {
          Swal.fire({ title: 'Erro', text: 'Não foi possível eliminar o preço.', icon: 'error' });
        }
      });
    };

    const createPrice = async () => {
      if (!newPrice.value.duration_days || !newPrice.value.price) {
        Swal.fire({ title: 'Preencha duração e preço.', icon: 'warning' });
        return;
      }
      savingPrice.value = true;
      try {
        const res = await store.dispatch('plans/createPlanPrice', {
          planId: selectedPlan.value.id,
          payload: { ...newPrice.value },
        });
        planPrices.value = await store.dispatch('plans/getPlanPrices', selectedPlan.value.id);
        newPrice.value = { duration_days: null, price: null, currency: 'MZN' };
        res; // suppress lint warning
      } catch {
        Swal.fire({ title: 'Erro', text: 'Não foi possível criar o preço.', icon: 'error' });
      } finally {
        savingPrice.value = false;
      }
    };

    onMounted(fetchPlans);

    return {
      loading, saving, planDialog, pricesDialog, editingPlan, selectedPlan,
      planForm, planPrices, pricesLoading, savingPrice, newPrice,
      plans, activeStates, listingTypeMeta,
      autoSlug, openCreate, openEdit, openPrices,
      addPerk, removePerk, addPrice, removePrice,
      savePlan, toggleActive, confirmDelete,
      savePrice, deletePrice, createPrice,
    };
  },
};
</script>

<style scoped>
/* ── Page ─────────────────────────────────────────────── */
.plans-page { background: #f4f6fb; min-height: 100vh; }

/* ── Header ───────────────────────────────────────────── */
.page-header { background: #0d1b2a; padding: 20px 24px; }
.page-header-inner {
  display: flex; align-items: center; justify-content: space-between;
  max-width: 1100px; margin: 0 auto; flex-wrap: wrap; gap: 12px;
}
.page-title { font-size: 20px; font-weight: 700; color: #fff; }
.page-subtitle { font-size: 12px; color: rgba(212,175,55,.75); margin-top: 2px; letter-spacing: .4px; }
.header-actions { display: flex; gap: 8px; }

.btn-primary {
  display: flex; align-items: center; padding: 9px 18px;
  background: #d4af37; color: #0d1b2a; border: none; border-radius: 10px;
  font-size: 13px; font-weight: 700; cursor: pointer; letter-spacing: .3px;
  transition: background .15s;
}
.btn-primary:hover { background: #e2c252; }

/* ── Content ──────────────────────────────────────────── */
.content-wrapper { max-width: 1100px; margin: 28px auto; padding: 0 16px 60px; }

/* ── Empty ────────────────────────────────────────────── */
.empty-state { text-align: center; padding: 64px 16px; background: #fff; border-radius: 16px; border: 1px solid #e8ecf4; }
.empty-text { font-size: 15px; color: #94a3b8; margin-top: 12px; }

/* ── Plans table ──────────────────────────────────────── */
.plans-list { background: #fff; border-radius: 16px; border: 1px solid #e8ecf4; overflow: hidden; }
.plans-table-head, .plan-row {
  display: grid;
  grid-template-columns: 1fr 130px 80px 90px 70px 100px 110px;
  align-items: center; gap: 12px; padding: 12px 20px;
}
.plans-table-head {
  background: #f8fafc; font-size: 11px; font-weight: 700;
  color: #94a3b8; text-transform: uppercase; letter-spacing: .8px;
  border-bottom: 1px solid #e8ecf4;
}
.plan-row { border-bottom: 1px solid #f1f5f9; }
.plan-row:last-child { border-bottom: none; }
.plan-name { font-size: 14px; font-weight: 600; color: #0f172a; }
.plan-desc { font-size: 11.5px; color: #94a3b8; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.col-name { min-width: 0; }
.col-slug, .col-centre, .col-actions { text-align: center; }
.slug-code { font-size: 11.5px; background: #f1f5f9; padding: 2px 8px; border-radius: 6px; color: #475569; }
.col-actions { display: flex; gap: 4px; justify-content: center; }

/* ── Badges ───────────────────────────────────────────── */
.badge {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600;
}
.badge--warn  { background: rgba(245,158,11,.12); color: #d97706; }
.badge--muted { background: rgba(148,163,184,.12); color: #94a3b8; }
.badge--info  { background: rgba(59,130,246,.1);  color: #2563eb; }

/* ── Action icons ─────────────────────────────────────── */
.action-icon {
  width: 32px; height: 32px; border-radius: 8px; border: 1px solid transparent;
  background: transparent; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background .15s, border-color .15s; position: relative;
}
.action-icon--primary { color: #4f46e5; }
.action-icon--primary:hover { background: rgba(99,102,241,.1); border-color: rgba(99,102,241,.2); }
.action-icon--prices { color: #0ea5e9; }
.action-icon--prices:hover { background: rgba(14,165,233,.1); border-color: rgba(14,165,233,.2); }
.action-icon--danger { color: #dc2626; }
.action-icon--danger:hover { background: rgba(239,68,68,.1); border-color: rgba(239,68,68,.2); }

/* ── Skeleton ─────────────────────────────────────────── */
.skeleton-row { margin-bottom: 8px; }

/* ── Dialog ───────────────────────────────────────────── */
.plan-dialog { max-width: 760px; border-radius: 18px !important; display: flex; flex-direction: column; }
.dialog-header { background: #0d1b2a; padding: 20px 24px; }
.dialog-title { font-size: 17px; font-weight: 700; color: #fff; }
.dialog-body { flex: 1; overflow-y: auto; padding: 24px; background: #f8fafc; }
.dialog-footer { background: #fff; border-top: 1px solid #e8ecf4; padding: 16px 24px; }

.form-section { background: #fff; border: 1px solid #e8ecf4; border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.form-section-title { font-size: 13px; font-weight: 700; color: #0f172a; text-transform: uppercase; letter-spacing: .6px; margin-bottom: 14px; }
.form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.form-row-3 { display: grid; grid-template-columns: 100px 1fr 1fr; gap: 12px; align-items: center; }
.toggles-row { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }

/* ── Perks ────────────────────────────────────────────── */
.perk-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.flex-1 { flex: 1; }
.perk-remove {
  width: 28px; height: 28px; border-radius: 6px; border: 1px solid #fecaca;
  background: rgba(239,68,68,.08); color: #dc2626; cursor: pointer;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.btn-add-perk {
  display: inline-flex; align-items: center; padding: 6px 14px;
  border: 1px dashed #cbd5e1; border-radius: 8px; background: transparent;
  color: #64748b; font-size: 12.5px; font-weight: 600; cursor: pointer;
}
.btn-add-perk:hover { border-color: #94a3b8; color: #475569; }

/* ── Price table ──────────────────────────────────────── */
.price-table-head {
  display: flex; gap: 12px; font-size: 11px; font-weight: 700;
  color: #94a3b8; text-transform: uppercase; letter-spacing: .8px;
  padding-bottom: 8px; border-bottom: 1px solid #e8ecf4; margin-bottom: 10px;
}
.price-table-head span { flex: 1; }
.price-table-row, .price-edit-row { display: flex; gap: 8px; align-items: center; margin-bottom: 8px; }
.price-edit-row { padding: 6px 0; border-bottom: 1px solid #f1f5f9; }

.form-section-copy { margin: -6px 0 16px; color: #64748b; font-size: 12px; line-height: 1.5; }
.listing-feature-grid { display: grid; gap: 14px; }
.listing-feature-card { border: 1px solid #dfe5ee; border-radius: 13px; background: #fff; overflow: hidden; transition: opacity .18s, border-color .18s; }
.listing-feature-card--disabled { opacity: .62; border-style: dashed; }
.listing-feature-card__header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 14px 16px; background: #f8fafc; border-bottom: 1px solid #e8ecf4; }
.listing-feature-card__identity { display: flex; align-items: center; gap: 11px; min-width: 0; color: #d4af37; }
.listing-feature-card__identity div { display: grid; gap: 2px; }
.listing-feature-card__identity strong { color: #0d1b2a; font-size: 14px; }
.listing-feature-card__identity small { color: #64748b; font-size: 11px; }
.listing-feature-card__body { padding: 16px; }
.listing-feature-card__toggles { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px 12px; margin-top: 10px; }

@media (max-width: 680px) {
  .form-row-2, .listing-feature-card__toggles { grid-template-columns: 1fr; }
  .listing-feature-card__header { align-items: flex-start; flex-direction: column; }
}
</style>
